import type { UnitSystem } from "@/atoms/units";
import { getDirectionLabel } from "@/lib/buoys";
import type { HourlyMarine } from "@/lib/marine";
import { fontStack } from "@/theme";
export { tideAtTime } from "@/lib/tides";
import { convertHeight, heightUnit } from "@/lib/units";
import type { HourlyWind } from "@/lib/weather";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { format, parseISO } from "date-fns";

interface ForecastDetailPanelProps {
  marine: HourlyMarine;
  wind: HourlyWind | null;
  tide: { height: number; trend: "rising" | "falling" | "steady" } | null;
  units: UnitSystem;
  shoreNormal: number | null;
}

/**
 * Detail readout shown under the 5-day chart — the picked hour's full
 * state as a row of labeled mono numbers, separated by hairline rules.
 * Per DESIGN.md: no card, no border, whitespace does structural work.
 */
export default function ForecastDetailPanel({
  marine,
  wind,
  tide,
  units,
  shoreNormal,
}: ForecastDetailPanelProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const parsed = parseISO(marine.time);
  const timeLabel = format(parsed, isMobile ? "h a" : "h:mm a");
  const dayLabel = format(parsed, "EEE M/d");

  const windSpeed =
    wind != null
      ? units === "imperial"
        ? (wind.windSpeedKmh * 0.621371).toFixed(0)
        : wind.windSpeedKmh.toFixed(0)
      : null;
  const windUnit = units === "imperial" ? "mph" : "km/h";

  const height = convertHeight(marine.waveHeightM, units).toFixed(1);
  const secondary =
    marine.swellHeightM != null && marine.swellPeriod != null
      ? `${
          marine.swellDirection != null ? getDirectionLabel(marine.swellDirection) : ""
        } ${convertHeight(marine.swellHeightM, units).toFixed(1)}${heightUnit(
          units,
        )} @ ${marine.swellPeriod.toFixed(0)}s`
      : null;

  const primary =
    marine.wavePeriod != null
      ? `${getDirectionLabel(marine.waveDirection)} ${height}${heightUnit(
          units,
        )} @ ${marine.wavePeriod.toFixed(0)}s`
      : `${getDirectionLabel(marine.waveDirection)} ${height}${heightUnit(units)}`;

  return (
    <Box
      sx={{
        mt: 2,
        pt: 2,
        pb: 1.5,
        borderTop: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography variant="overline" component="div" sx={{ color: "ink.muted", mb: 1.5 }}>
        {dayLabel} · {timeLabel}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "auto 1fr",
            md: `auto repeat(${3 + (tide ? 1 : 0)}, 1fr)`,
          },
          columnGap: { xs: 2, md: 3 },
          rowGap: 2,
          alignItems: "center",
        }}
      >
        {wind && (
          <WindCompass
            directionDeg={wind.windDirectionDeg}
            shoreNormal={shoreNormal}
            size={isMobile ? 56 : 64}
            ink={theme.palette.ink.main}
            muted={theme.palette.ink.muted}
            rule={theme.palette.rule.strong}
            accent={theme.palette.accent.main}
          />
        )}

        <DetailStat
          label="Wind"
          value={
            wind && windSpeed != null
              ? `${getDirectionLabel(wind.windDirectionDeg)} ${windSpeed}${windUnit}`
              : "—"
          }
        />

        <DetailStat label="Primary" value={primary} />

        <DetailStat label="Secondary" value={secondary ?? "—"} />

        {tide && (
          <DetailStat
            label="Tide"
            value={`${convertHeight(tide.height, units).toFixed(1)} ${heightUnit(units)} ${
              tide.trend === "rising" ? "↑" : tide.trend === "falling" ? "↓" : "·"
            }`}
          />
        )}
      </Box>
    </Box>
  );
}

function DetailStat({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography
        variant="overline"
        component="div"
        sx={{ color: "ink.muted", mb: 0.25 }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: fontStack.mono,
          fontSize: { xs: "0.9375rem", md: "1rem" },
          fontWeight: 500,
          color: "ink.main",
          lineHeight: 1.25,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/**
 * Compass glyph. Arrow points toward where the wind is coming FROM
 * (meteorological convention), so it agrees with the "WNW 12 mph" text
 * label next to it. The shaded arc marks the onshore half of the
 * compass — when the arrow points *into* the shaded region, the wind
 * is coming FROM the sea (onshore / chop).
 *
 * SVG coordinate space has +y DOWN. Cardinal north = negative y = top.
 * A compass angle θ (from vertical, clockwise) maps to math-angle θ-90.
 */
function WindCompass({
  directionDeg,
  shoreNormal,
  size,
  ink,
  muted,
  rule,
  accent,
}: {
  directionDeg: number;
  shoreNormal: number | null;
  size: number;
  ink: string;
  muted: string;
  rule: string;
  accent: string;
}) {
  const r = size / 2;
  const arrowRad = ((directionDeg - 90) * Math.PI) / 180;
  const ax = r + (r - 6) * Math.cos(arrowRad);
  const ay = r + (r - 6) * Math.sin(arrowRad);

  return (
    <Box sx={{ width: size, height: size, position: "relative" }}>
      <svg width={size} height={size}>
        <circle cx={r} cy={r} r={r - 1} fill="none" stroke={rule} strokeWidth={1} />
        {shoreNormal != null && <OnshoreArc r={r} shoreNormal={shoreNormal} />}
        <line
          x1={r}
          y1={r}
          x2={ax}
          y2={ay}
          stroke={accent}
          strokeWidth={1.5}
          strokeLinecap="round"
        />
        <circle cx={ax} cy={ay} r={2.5} fill={accent} />
        {(["N", "E", "S", "W"] as const).map((dir, i) => {
          const angle = ((i * 90 - 90) * Math.PI) / 180;
          const lx = r + (r - 10) * Math.cos(angle);
          const ly = r + (r - 10) * Math.sin(angle) + 3;
          return (
            <text
              key={dir}
              x={lx}
              y={ly}
              textAnchor="middle"
              fontSize={9}
              fontFamily={fontStack.sans}
              fontWeight={500}
              fill={i === 0 ? ink : muted}
            >
              {dir}
            </text>
          );
        })}
      </svg>
    </Box>
  );
}

/** Shaded arc showing the "onshore" half of the compass (±90° around shoreNormal). */
function OnshoreArc({ r, shoreNormal }: { r: number; shoreNormal: number }) {
  const start = ((shoreNormal - 90 - 90) * Math.PI) / 180;
  const end = ((shoreNormal + 90 - 90) * Math.PI) / 180;
  const x1 = r + (r - 1) * Math.cos(start);
  const y1 = r + (r - 1) * Math.sin(start);
  const x2 = r + (r - 1) * Math.cos(end);
  const y2 = r + (r - 1) * Math.sin(end);
  const path = `M ${r} ${r} L ${x1} ${y1} A ${r - 1} ${r - 1} 0 0 1 ${x2} ${y2} Z`;
  return <path d={path} fill="rgba(200,51,31,0.08)" />;
}
